import * as React from "react";

import { cn } from "../../lib/utils";

/**
 * Suppress text selection when a label is double-clicked.
 *
 * This is the *only* thing `@radix-ui/react-label` did beyond rendering a
 * `<label>`, and it is worth keeping: a native label selects its caption on
 * double-click, which reads as an accidental highlight every time someone
 * double-clicks a form field's text.
 *
 * The `closest` check is the half that matters and the half that is easy to
 * drop. When the label *wraps* its control, a double-click inside that control
 * must keep native word-selection — without this bail-out a wrapped input
 * silently stops being double-clickable, a regression nobody would attribute to
 * a label component.
 *
 * Not a Radix quirk: Base UI implements the same guard, character for
 * character, in `useLabel` under `native: true`.
 */
function suppressDoubleClickSelection(
  event: React.MouseEvent<HTMLLabelElement>,
) {
  if (
    event.target instanceof Element &&
    event.target.closest("button, input, select, textarea")
  ) {
    return;
  }
  if (!event.defaultPrevented && event.detail > 1) {
    event.preventDefault();
  }
}

/**
 * Base UI has no standalone Label — only `Field.Label`, which is built for
 * `Field.Root` composition. It does render standalone, but it stamps a
 * generated `id` on every label that isn't given one (`useBaseUiId`), which
 * across 1697 usages is a DOM change we get nothing back for, plus
 * field-validity state attributes that mean nothing without a `Field.Root`.
 *
 * So this migration drops `@radix-ui/react-label` for the native element rather
 * than swapping one dependency for another — the only component in the
 * initiative that removes a dependency without adding one.
 */
function Label({
  className,
  vertical = false,
  onMouseDown,
  ...props
}: React.ComponentProps<"label"> & { vertical?: boolean }) {
  return (
    <label
      data-slot="label"
      onMouseDown={(event) => {
        onMouseDown?.(event);
        suppressDoubleClickSelection(event);
      }}
      className={cn(
        "flex items-center gap-2 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50",
        vertical && "flex-col items-start",
        className,
      )}
      {...props}
    />
  );
}

export { Label };
